import { Component } from '@theme/component';
import { ThemeEvents, SlideshowSelectEvent, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * Move the grid / zoom-list child with `data-media-id` to the front so it
 * matches the selected variant's featured image. Section Rendering does not
 * expose `option_values` to Liquid `request.*`, so this runs on the client
 * after `VariantUpdateEvent`.
 *
 * @param {ParentNode | null} container
 * @param {string} childSelector
 * @param {string} idStr
 */
function moveFeaturedMediaIdFirst(container, childSelector, idStr) {
  if (!container) return;
  const children = Array.from(container.querySelectorAll(`:scope > ${childSelector}`));
  const featured = children.find((el) => el instanceof HTMLElement && el.dataset.mediaId === idStr);
  if (!featured || featured === children[0]) return;
  container.insertBefore(featured, container.firstElementChild);
}

/**
 * @param {HTMLElement} root - `<media-gallery>` element
 * @param {string | number} featuredMediaId - `variant.featured_media.id` from VariantUpdateEvent
 */
function promoteVariantFeaturedMedia(root, featuredMediaId) {
  if (featuredMediaId == null || featuredMediaId === '') return;

  const idStr = String(featuredMediaId);
  /** Numeric Shopify ids are safe inside attribute selectors */
  const innerSel = `[data-media-id="${idStr}"]`;

  const grid = root.querySelector('ul.media-gallery__grid');
  moveFeaturedMediaIdFirst(grid, 'li', idStr);

  const slideshowEl = root.querySelector('slideshow-component');
  if (!(slideshowEl instanceof HTMLElement)) return;

  const scroller = slideshowEl.querySelector('slideshow-slides');
  if (!scroller) return;

  const slides = Array.from(scroller.querySelectorAll(':scope > slideshow-slide'));
  const idx = slides.findIndex((slide) => slide.querySelector(innerSel));
  if (idx <= 0) return;

  queueMicrotask(() => {
    /** @type {{ select?: (input: number, event?: Event, options?: object) => void }} */
    const api = slideshowEl;
    api.select?.(idx, undefined, { animate: false });
  });
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const featuredMediaId = event.detail.resource?.featured_media?.id;

    this.replaceWith(newMediaGallery);

    if (featuredMediaId != null && featuredMediaId !== '') {
      queueMicrotask(() => {
        promoteVariantFeaturedMedia(newMediaGallery, featuredMediaId);
      });
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Pause media on inactive slides whenever the selected slide changes.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    const activeSlide = event.detail.slide;
    if (!(activeSlide instanceof HTMLElement)) return;

    const slides = this.slideshow?.refs?.slides;
    if (!slides?.length) return;

    for (const slide of slides) {
      if (slide === activeSlide) continue;
      this.#pauseSlideMedia(slide);
    }
  };

  /**
   * Pauses all playable media inside a slide.
   * @param {HTMLElement} slide
   */
  #pauseSlideMedia(slide) {
    slide.querySelectorAll('deferred-media, product-model').forEach((media) => {
      if (media instanceof HTMLElement && typeof media.pauseMedia === 'function') {
        media.pauseMedia();
      }
    });

    slide.querySelectorAll('video').forEach((video) => {
      if (video instanceof HTMLVideoElement) {
        video.pause();
      }
    });

    slide.querySelectorAll('iframe[data-video-type]').forEach((iframe) => {
      if (!(iframe instanceof HTMLIFrameElement)) return;

      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"pauseVideo","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    });
  }

  /**
   * Reveals all product media when the gallery has more than four items.
   *
   * @param {Event} [event]
   */
  expandAllMedia(event) {
    event?.preventDefault();

    this.classList.add('media-gallery--expanded');

    this.querySelectorAll('.media-gallery__grid-item--collapsed').forEach((item) => {
      item.classList.remove('media-gallery__grid-item--collapsed');
      item.removeAttribute('hidden');
    });

    this.querySelectorAll('slideshow-slide[hidden]').forEach((slide) => {
      slide.removeAttribute('hidden');
    });

    const { showAllButton } = this.refs;
    if (showAllButton instanceof HTMLElement) {
      showAllButton.hidden = true;
      showAllButton.setAttribute('aria-expanded', 'true');
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
